export type ActivityEnvironment =
  | 'web'
  | 'windows'
  | 'linux-x11'
  | 'linux-wayland';

export type ActivityAssistantState =
  | 'active'
  | 'paused'
  | 'disabled'
  | 'unsupported'
  | 'error';

export interface ActivityStatus {
  environment: ActivityEnvironment;
  state: ActivityAssistantState;
  supported: boolean;
  message?: string | null;
  lastEventAt?: string | null;
}

export interface ActivitySettings {
  enabled: boolean;
  paused: boolean;
  startAtLogin: boolean;
  retentionDays: 1 | 7 | 30;
  idleThresholdMinutes: number;
  excludedApps: string[];
}

export interface ActivitySegment {
  id: string;
  userId: string;
  processName: string;
  applicationName?: string | null;
  windowTitle: string;
  startedAt: string;
  endedAt: string;
  ignored?: boolean;
  importedTaskId?: string | null;
}

export interface ActivityRule {
  id: string;
  processName?: string | null;
  titleContains?: string | null;
  projectId?: string | null;
  suggestedTitle?: string | null;
}

export interface ActivityDiagnostics {
  generatedAt: string;
  environment: ActivityEnvironment;
  state: ActivityAssistantState;
  supported: boolean;
  message?: string | null;
  lastEventAt?: string | null;
  collectorVersion: string;
  retentionDays: 1 | 7 | 30;
  idleThresholdMinutes: number;
  idleDetectionAvailable: boolean;
  segmentCount: number;
  ruleCount: number;
  importCount: number;
  /** Always false. Included so support exports can assert the privacy contract. */
  includesWindowTitles: false;
}

export type ActivitySegmentChanges = Partial<
  Pick<ActivitySegment, 'startedAt' | 'endedAt' | 'ignored'>
>;

export type SaveActivityRuleInput = Omit<ActivityRule, 'id'> & { id?: string };

export interface ActivityBridge {
  getStatus(): Promise<ActivityStatus>;
  getSettings(): Promise<ActivitySettings>;
  updateSettings(settings: Partial<ActivitySettings>): Promise<ActivitySettings>;
  setEnabled(enabled: boolean): Promise<ActivityStatus>;
  setPaused(paused: boolean): Promise<ActivityStatus>;
  listSegments(input: { date: string; userId: string }): Promise<ActivitySegment[]>;
  updateSegment(id: string, changes: ActivitySegmentChanges): Promise<ActivitySegment>;
  deleteSegments(ids: string[]): Promise<void>;
  deleteAllActivityData(): Promise<void>;
  markSegmentImported(segmentId: string, taskId: string): Promise<void>;
  listRules(): Promise<ActivityRule[]>;
  saveRule(rule: SaveActivityRuleInput): Promise<ActivityRule>;
  deleteRule(id: string): Promise<void>;
  setActiveUser(userId: string | null): Promise<ActivityStatus>;
  exportDiagnostics(): Promise<string>;
}

export const DEFAULT_ACTIVITY_SETTINGS: ActivitySettings = {
  enabled: false,
  paused: false,
  startAtLogin: false,
  retentionDays: 7,
  idleThresholdMinutes: 5,
  excludedApps: [],
};
